import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import SingleCard from "../components/Reusable/SingleCard";

import {
  totalEmployee,
  employementVisa,
  employeePassport,
  emiratesId,
  medicalInsurance,
  dmccAccessCard,
  labourCard,
  newEmployee,
} from "../components/Reusable/CardData";

// Zustand Stores
import useCompanyStore from "../store/useCompanyStore";
import useEmployeeStore from "../store/useEmployeeStore";
import useAttendanceStore from "../store/useAttendanceStore";
import useLeaveStore from "../store/useLeaveStore";
import useEmployeeExitStore from "../store/useEmployeeExitStore";
import useSelectedCompanyStore from "../store/useSelectedCompanyStore";

import { getDocumentExpiryStats } from "../calculations/documentExpiryCalculations";
import { getAccessCardExpiryStats } from "../calculations/accessCardCalculations";
import { getAttendanceStats } from "./../calculations/attendanceCalculations";
import { getLeaveStats } from "../calculations/leaveCalculations";
import {
  getNewEmployeesLast30Days,
  getEmployeeExitLast30Days,
} from "../calculations/employeeCalculations";
import { createDocumentCards } from "../config/documentCardsConfig";
import DocumentCardsSection from "../components/dashboard/DocumentCardsSection";

import DashboardHeader from "../components/dashboard/DashboardHeader.jsx";
import LeaveRequestSection from "../components/dashboard/LeaveRequestSection.jsx";
import DashboardStatsSection from "../components/dashboard/DashboardStatsSection.jsx";
import AbsentWithoutLeaveSection from "../components/dashboard/AbsentWithoutLeaveSection.jsx";
import { handleDocumentNavigation } from "../utils/navigation/documentNavigation.js";
import SingleCardSkeleton from "../components/common/SingleCardSkelton.jsx";
import DocumentsCardSkeletonList from "../components/common/DocumentsCardSkeletonList.jsx";

const Dashboard = () => {
  const permissions = useMemo(
    () => JSON.parse(localStorage.getItem("userPermissions")),
    [],
  );

  const [totalLeaveRequest, setTotalLeaveRequest] = useState(0);

  const { companyList, fetchCompanies } = useCompanyStore();
  const {
    employeeData,
    loading: employeeLoading,
    fetchEmployees,
  } = useEmployeeStore();
  const { attendanceData, fetchAttendance } = useAttendanceStore();
  const { leaveData, fetchLeaveData } = useLeaveStore();
  const { employeeExitData, fetchEmployeeExitData } = useEmployeeExitStore();
  const { selectedCompany, setSelectedCompany } = useSelectedCompanyStore();

  const navigate = useNavigate();

  let punchingTime;

  if (selectedCompany?.name === "SCALEX IT SOLUTIONS") {
    punchingTime = "10:15:00";
  } else if (selectedCompany?.name === "GREEN APPLE SOFTWARE TRADING LLC") {
    punchingTime = "09:45:00";
  } else if (selectedCompany?.name === "BLUE BIRD SIM CARDS TRADING LLC") {
    punchingTime = "09:45:00";
  }

  const activeEmployeeData = useMemo(() => {
    return employeeData.filter((employee) => employee.is_active === 1);
  }, [employeeData]);

  useEffect(() => {
    fetchCompanies();

    fetchEmployees({
      selectedCompany: selectedCompany?.name || "",
      permissions,
    });

    fetchAttendance({
      selectedCompany: selectedCompany?.name || "",
      permissions,
    });

    fetchLeaveData({
      selectedCompany: selectedCompany?.name || "",
      permissions,
    });

    fetchEmployeeExitData({
      selectedCompany: selectedCompany?.name || "",
    });
  }, [
    selectedCompany,
    permissions,
    fetchCompanies,
    fetchEmployees,
    fetchAttendance,
    fetchLeaveData,
    fetchEmployeeExitData,
  ]);

  const updateTotalLeaveRequest = (totalLeave) => {
    setTotalLeaveRequest(totalLeave);
  };

  const onChange = (company) => {
    if (!company) {
      setSelectedCompany(null);
      return;
    }

    setSelectedCompany({
      id: company.id,
      name: company.company_name,
    });
  };

  const totalEmployees = activeEmployeeData.length;

  const visaStats = getDocumentExpiryStats(activeEmployeeData, "Visa");

  const passportStats = getDocumentExpiryStats(activeEmployeeData, "Passport");

  const eidStats = getDocumentExpiryStats(activeEmployeeData, "EID");

  const insuranceStats = getDocumentExpiryStats(
    activeEmployeeData,
    "Insurance Card",
  );

  const accessCardStats = getAccessCardExpiryStats(activeEmployeeData);

  const labourCardStats = getDocumentExpiryStats(
    activeEmployeeData,
    "Labour Card",
  );

  const attendanceStats = getAttendanceStats(attendanceData, punchingTime);

  const leaveStats = getLeaveStats(leaveData);

  const newEmployeeStats = getNewEmployeesLast30Days(activeEmployeeData);

  const employeeExitStats = getEmployeeExitLast30Days(employeeExitData);

  const documentCards = createDocumentCards({
    visaStats,
    passportStats,
    eidStats,
    insuranceStats,
    accessCardStats,
    labourCardStats,
    employementVisa,
    employeePassport,
    emiratesId,
    medicalInsurance,
    dmccAccessCard,
    labourCard,
  });

  const moveToLateLogins = () => {
    const stateData = {
      lateLoginData: attendanceStats.lateLoginEmployees,
      selectedCompany: selectedCompany?.name,
    };

    navigate("/latelogins", {
      state: stateData,
    });
  };

  const moveToNewEmployeeList = () => {
    const stateData = {
      employeeList: newEmployeeStats.employees,
      selectedCompany: selectedCompany?.name,
    };
    navigate("/newemployee", { state: stateData });
  };

  const moveToEmployeeLeft = () => {
    const stateData = {
      employeeExitList: employeeExitStats.employees,
      selectedCompany: selectedCompany?.name,
    };
    navigate("/employeeleft", { state: stateData });
  };

  // debug logs
  // console.log("employee data", activeEmployeeData)
  console.log("employeeLoading", employeeLoading);

  return (
    <div className="w-full px-[30px] pt-0 pb-[50px] bg-[var(--primary-color)]">
      <DashboardHeader
        companyList={companyList}
        selectedCompany={selectedCompany}
        onChange={onChange}
        totalEmployees={totalEmployees}
        newEmployeeStats={newEmployeeStats}
        navigate={navigate}
        moveToNewEmployeeList={moveToNewEmployeeList}
      />

      <div className="flex flex-col gap-[30px]">
        {/* Top Section */}
        <section className="grid grid-cols-[2fr_1fr] gap-[1.813rem] items-stretch">
          {/* Top Left Section */}
          <div className="flex flex-col h-full">
            {/* Total Employees and New Employee Cards Section */}
            <div className="grid grid-cols-2 mb-2 gap-2">
              <div
                className="flex gap-4"
                onClick={() => navigate("/empmanagement")}
              >
                {employeeLoading ? (
                  <SingleCardSkeleton />
                ) : (
                  <SingleCard
                    selectedCompany={selectedCompany}
                    item={{
                      ...totalEmployee,
                      count: totalEmployees,
                      month: `+ ${newEmployeeStats.count} this month`,
                      linkTo: "/empmanagement",
                    }}
                  />
                )}
              </div>

              <div className="flex gap-4" onClick={moveToNewEmployeeList}>
                {employeeLoading ? (
                  <SingleCardSkeleton />
                ) : (
                  <SingleCard
                    item={{
                      ...newEmployee,
                      count: newEmployeeStats.count,
                      month: `+ ${newEmployeeStats.count} this month`,
                    }}
                  />
                )}
              </div>
            </div>

            {/* Documents Expiry Display Section */}
            <div>
              {employeeLoading ? (
                <DocumentsCardSkeletonList count={5} />
              ) : (
                <DocumentCardsSection
                  documentCards={documentCards}
                  handleDocumentNavigation={(type, stats, route, stateKey) =>
                    handleDocumentNavigation({
                      type,
                      stats,
                      route,
                      stateKey,
                      navigate,
                    })
                  }
                />
              )}
            </div>
          </div>

          {/* Leave Request Display Section */}
          <div className="">
            <LeaveRequestSection
              totalLeaveRequest={totalLeaveRequest}
              selectedCompany={selectedCompany}
              updateTotalLeaveRequest={updateTotalLeaveRequest}
              navigate={navigate}
            />
          </div>
        </section>

        {/* Bottom Section */}
        <section className="grid grid-cols-[2fr_1fr] gap-[1.813rem] items-stretch">
          {/* Bottom Left */}
          <DashboardStatsSection
            leaveStats={leaveStats}
            employeeExitStats={employeeExitStats}
            attendanceStats={attendanceStats}
            moveToEmployeeLeft={moveToEmployeeLeft}
            moveToLateLogins={moveToLateLogins}
          />

          {/* Bottom Right */}
          <AbsentWithoutLeaveSection
            selectedCompany={selectedCompany}
            attendanceData={attendanceData}
            leaveData={leaveData}
          />
        </section>
      </div>
    </div>
  );
};

export default Dashboard;
